import { Component, Input, OnInit } from '@angular/core';
import { Datastore } from '@freelancer/datastore';
import {
  Enterprise,
  InsigniaType,
  MembershipPackage,
  ProjectViewUsersCollection,
  UserInsigniasCollection,
} from '@freelancer/datastore/collections';
import { Feature } from '@freelancer/feature-flags';
import {
  Badge,
  BadgeType,
  CorporateBadgeType,
  MembershipBadgeType,
  PreferredFreelancerBadgeType,
  UserBadgeType,
} from '@freelancer/ui/badge';
import { Margin } from '@freelancer/ui/margin';
import { TooltipPosition } from '@freelancer/ui/tooltip';
import { UsernameSize } from '@freelancer/ui/username';
import * as Rx from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-user-profile-summary-name',
  template: `
    <fl-bit class="NameContainer" [flMarginBottom]="Margin.XXSMALL">
      <fl-username
        class="NameContainer-name"
        [displayName]="publicName"
        [flMarginBottom]="Margin.XXXSMALL"
        [flMarginBottomTablet]="Margin.NONE"
        [flMarginRightTablet]="Margin.SMALL"
        [size]="UsernameSize.XXLARGE"
        [username]="username"
      ></fl-username>

      <fl-bit
        class="NameContainer-badges"
        [flMarginBottom]="Margin.XXXSMALL"
        [flMarginBottomTablet]="Margin.NONE"
      >
        <ng-container *flFeature="Feature.MEMBERSHIPS">
          <fl-tooltip
            class="NameContainer-badge"
            *ngIf="isLoggedIn && isCorporate"
            message="Corporate Account"
            i18n-message="Tooltip for a corporate account badge"
            [position]="TooltipPosition.TOP_CENTER"
          >
            <fl-badge [badge]="corporateBadge"></fl-badge>
          </fl-tooltip>
          <ng-container
            *ngIf="membershipPackage$ | flAsync as membershipPackage"
          >
            <fl-tooltip
              *ngIf="
                membershipPackage.name !== 'free' && membershipPackage.badgeType
              "
              class="NameContainer-badge"
              message="{{ membershipPackage.name | titlecase }} Membership"
              i18n-message="UserMembershipBadgeTooltipMessage"
              [position]="TooltipPosition.TOP_CENTER"
            >
              <fl-badge
                [badge]="getBadge(membershipPackage.badgeType)"
              ></fl-badge>
            </fl-tooltip>
          </ng-container>
        </ng-container>

        <ng-container *flFeature="Feature.PREFERRED_FREELANCER_PROGRAM">
          <fl-tooltip
            *ngIf="isPreferredFreelancer"
            class="NameContainer-badge"
            message="Preferred Freelancer"
            i18n-message="Tooltip for a Preferred Freelancer badge"
            [position]="TooltipPosition.TOP_CENTER"
          >
            <fl-badge [badge]="pfBadge"></fl-badge>
          </fl-tooltip>
        </ng-container>

        <app-freelancer-verified-badge
          *ngIf="isFreelancerVerified"
          class="NameContainer-badge"
          [tooltipPosition]="TooltipPosition.TOP_CENTER"
        ></app-freelancer-verified-badge>

        <fl-tooltip
          *ngIf="showDoGooderBadge$ | flAsync"
          class="NameContainer-badge"
          message="Do Gooder"
          i18n-message="Tooltip for a Do Gooder badge"
          [position]="TooltipPosition.TOP_CENTER"
        >
          <fl-badge [badge]="doGooderBadge"></fl-badge>
        </fl-tooltip>
      </fl-bit>
    </fl-bit>
  `,
  styleUrls: ['./user-profile-summary-name.component.scss'],
})
export class UserProfileSummaryNameComponent implements OnInit {
  BadgeType = BadgeType;
  CorporateBadgeType = CorporateBadgeType;
  Feature = Feature;
  Margin = Margin;
  TooltipPosition = TooltipPosition;
  UserBadgeType = UserBadgeType;
  UsernameSize = UsernameSize;

  @Input() publicName: string;
  @Input() username: string;
  @Input() profileUserId: number;
  @Input() isCorporate: boolean;
  @Input() isPreferredFreelancer: boolean;
  @Input() isFreelancerVerified: boolean;
  @Input() isLoggedIn: boolean;
  @Input() isViewerProfileOwner: boolean;
  @Input() enterpriseIds: readonly number[];

  membershipPackage$: Rx.Observable<MembershipPackage | undefined>;
  showDoGooderBadge$: Rx.Observable<boolean>;

  corporateBadge: Badge = {
    type: BadgeType.CORPORATE,
    badge: CorporateBadgeType.CORPORATE,
  };

  doGooderBadge: Badge = {
    type: BadgeType.USER,
    badge: UserBadgeType.DO_GOODER,
  };

  pfBadge: Badge;

  constructor(private datastore: Datastore) {}

  ngOnInit() {
    this.showDoGooderBadge$ = this.datastore
      .collection<UserInsigniasCollection>('userInsignias', query =>
        query.where('userId', '==', this.profileUserId),
      )
      .valueChanges()
      .pipe(
        map(userInsignias =>
          userInsignias.some(i => i.name === InsigniaType.DO_GOODER),
        ),
      );

    this.pfBadge = {
      type: BadgeType.PREFERRED_FREELANCER,
      badge: this.enterpriseIds?.includes(Enterprise.ARROW)
        ? PreferredFreelancerBadgeType.PREFERRED_ARROW_FREELANCER
        : PreferredFreelancerBadgeType.PREFERRED_FREELANCER,
    };

    this.membershipPackage$ =
      !this.isLoggedIn || this.isViewerProfileOwner
        ? this.datastore
            .document<ProjectViewUsersCollection>(
              'projectViewUsers',
              this.profileUserId,
            )
            .valueChanges()
            .pipe(map(projectViewUser => projectViewUser.membershipPackage))
        : Rx.of(undefined);
  }

  getBadge(badge: MembershipBadgeType): Badge {
    return {
      type: BadgeType.MEMBERSHIP,
      badge,
    };
  }
}
